import { dbService, firebaseInstance } from '../firebaseInstance';

const MAG_ROOT = 'magazines';
const MAG_ID = 'monthlyThreads';

export const createIssue = async (issueId, meta = {}) => {
  const [yearStr, monthStr] = String(issueId).split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const ref = dbService
    .collection(MAG_ROOT)
    .doc(MAG_ID)
    .collection('issues')
    .doc(issueId);
  const now = new Date();
  await ref.set(
    {
      issueId,
      title: meta.title || `${issueId} Monthly Threads`,
      year,
      month,
      coverImage: meta.coverImage || null,
      description: meta.description || null,
      isPublished: false,
      publishAt: null,
      editorIds: meta.editorIds || [],
      userIdList: [],
      createdAt: now,
      updatedAt: now,
    },
    { merge: true }
  );
  return issueId;
};

export const subscribeIssues = (onList, onError) => {
  return dbService
    .collection(MAG_ROOT)
    .doc(MAG_ID)
    .collection('issues')
    .onSnapshot(
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // 안정 정렬: year/month가 없을 경우 issueId(YYYY-MM)에서 파싱
        const parseYM = (it) => {
          const base = String(it.issueId || it.id || '');
          const y = typeof it.year === 'number' ? it.year : parseInt(base.split('-')[0] || '0', 10);
          const m = typeof it.month === 'number' ? it.month : parseInt(base.split('-')[1] || '0', 10);
          return { y: isNaN(y) ? 0 : y, m: isNaN(m) ? 0 : m };
        };
        list.sort((a, b) => {
          const A = parseYM(a);
          const B = parseYM(b);
          if (B.y !== A.y) return B.y - A.y;
          if (B.m !== A.m) return B.m - A.m;
          // tie-breaker by id desc
          return String(b.id).localeCompare(String(a.id));
        });
        onList && onList(list);
      },
      (err) => onError && onError(err)
    );
};

export const subscribeIssueItems = (issueId, onList, onError) => {
  return dbService
    .collection(MAG_ROOT)
    .doc(MAG_ID)
    .collection('issues')
    .doc(issueId)
    .collection('items')
    .orderBy('order', 'asc')
    .onSnapshot(
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        onList && onList(items);
      },
      (err) => onError && onError(err)
    );
};

export const addCollectionsToIssue = async (issueId, collections) => {
  // collections: Array<{ id: string, userID?: string | number }>
  if (!Array.isArray(collections) || collections.length === 0) return;
  const issueRef = dbService
    .collection(MAG_ROOT)
    .doc(MAG_ID)
    .collection('issues')
    .doc(issueId);
  const itemsRef = issueRef.collection('items');
  const now = new Date();

  // Resolve missing userIDs by reading collection docs
  const resolved = await Promise.all(
    collections.map(async (c) => {
      let userId = c.userID;
      if (!userId) {
        try {
          const snap = await dbService.collection('collection').doc(String(c.id)).get();
          if (snap.exists) {
            const data = snap.data() || {};
            userId = data.userID || data.userId || data.user_id || null;
          }
        } catch (_) {
          // ignore
        }
      }
      const userIdStr = userId != null ? String(userId) : '';
      return { id: String(c.id), userId: userIdStr };
    })
  );

  const current = await itemsRef.orderBy('order', 'desc').limit(1).get();
  let base = current.empty ? 0 : (current.docs[0].data().order || 0) + 1;

  const batch = dbService.batch();
  const userIdSet = new Set();

  resolved.forEach((c, idx) => {
    const docRef = itemsRef.doc();
    batch.set(docRef, {
      collectionId: c.id,
      userId: c.userId || null,
      order: base + idx,
      isFeatured: false,
      note: null,
      addedAt: now,
    });
    if (c.userId) userIdSet.add(c.userId);
  });

  // Merge quick list first for responsiveness
  const issueSnap = await issueRef.get();
  const prevUsers = issueSnap.exists && Array.isArray(issueSnap.data().userIdList)
    ? issueSnap.data().userIdList
    : [];
  const merged = Array.from(new Set([...prevUsers, ...Array.from(userIdSet)]));

  batch.set(
    issueRef,
    { userIdList: merged, updatedAt: now },
    { merge: true }
  );
  await batch.commit();

  // Recompute definitive user list from items to fix any missing/legacy fields
  try {
    const rest = await itemsRef.get();
    const recomputed = Array.from(
      new Set(rest.docs.map((d) => d.data().userId).filter(Boolean).map((v) => String(v)))
    );
    await issueRef.set({ userIdList: recomputed, updatedAt: new Date() }, { merge: true });
  } catch (_) {
    // ignore recompute failure
  }
};

export const removeItemFromIssue = async (issueId, itemId) => {
  const issueRef = dbService
    .collection(MAG_ROOT)
    .doc(MAG_ID)
    .collection('issues')
    .doc(issueId);
  const itemsRef = issueRef.collection('items');
  const itemSnap = await itemsRef.doc(itemId).get();
  if (!itemSnap.exists) return;
  await itemsRef.doc(itemId).delete();

  const rest = await itemsRef.get();
  const remainUsers = Array.from(
    new Set(rest.docs.map((d) => d.data().userId).filter(Boolean))
  );
  await issueRef.set({ userIdList: remainUsers, updatedAt: new Date() }, { merge: true });
};

export const reorderItems = async (issueId, orderedItemIds) => {
  const itemsRef = dbService
    .collection(MAG_ROOT)
    .doc(MAG_ID)
    .collection('issues')
    .doc(issueId)
    .collection('items');
  const batch = dbService.batch();
  orderedItemIds.forEach((id, idx) => {
    batch.set(itemsRef.doc(id), { order: idx }, { merge: true });
  });
  await batch.commit();
};

export const publishIssue = async (issueId, publish = true) => {
  const ref = dbService
    .collection(MAG_ROOT)
    .doc(MAG_ID)
    .collection('issues')
    .doc(issueId);
  await ref.set(
    {
      isPublished: publish,
      publishAt: publish ? new Date() : null,
      updatedAt: new Date(),
    },
    { merge: true }
  );
};

export const updateIssueMeta = async (issueId, updates) => {
  const ref = dbService
    .collection(MAG_ROOT)
    .doc(MAG_ID)
    .collection('issues')
    .doc(issueId);
  await ref.set({ ...updates, updatedAt: new Date() }, { merge: true });
};

// Ensure root issues/{issueId}.userIdList is in sync with items/*
export const recomputeIssueUserList = async (issueId) => {
  const issueRef = dbService
    .collection(MAG_ROOT)
    .doc(MAG_ID)
    .collection('issues')
    .doc(issueId);
  const itemsRef = issueRef.collection('items');
  const snap = await itemsRef.get();
  const list = Array.from(
    new Set(snap.docs.map((d) => d.data().userId).filter(Boolean).map((v) => String(v)))
  );
  await issueRef.set({ userIdList: list, updatedAt: new Date() }, { merge: true });
  return list;
};

// -------------------------------
// Cover stories & Sections API
// -------------------------------

const getIssueRef = (issueId) =>
  dbService
    .collection(MAG_ROOT)
    .doc(MAG_ID)
    .collection('issues')
    .doc(issueId);

export const setCoverStories = async (issueId, threadIds) => {
  const ref = getIssueRef(issueId);
  const unique = Array.from(new Set((Array.isArray(threadIds) ? threadIds : []).map((v) => String(v))));
  await ref.set({ coverStoryList: unique, updatedAt: new Date() }, { merge: true });
};

export const addSection = async (issueId, { title, description }) => {
  const ref = getIssueRef(issueId);
  const snap = await ref.get();
  const data = snap.exists ? snap.data() : {};
  const sections = Array.isArray(data.sectionList) ? data.sectionList : [];
  const newSection = {
    id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title: String(title || '').trim() || 'Untitled Section',
    description: String(description || '').trim() || '',
    threadIdList: [],
    order: sections.length,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
  const next = [...sections, newSection];
  await ref.set({ sectionList: next, updatedAt: new Date() }, { merge: true });
  return newSection.id;
};

export const updateSection = async (issueId, sectionId, updates) => {
  const ref = getIssueRef(issueId);
  const snap = await ref.get();
  if (!snap.exists) return;
  const data = snap.data() || {};
  const sections = Array.isArray(data.sectionList) ? data.sectionList : [];
  const next = sections.map((s) => (s.id === sectionId ? { ...s, ...updates, updatedAt: new Date() } : s));
  await ref.set({ sectionList: next, updatedAt: new Date() }, { merge: true });
};

export const removeSection = async (issueId, sectionId) => {
  const ref = getIssueRef(issueId);
  const snap = await ref.get();
  if (!snap.exists) return;
  const data = snap.data() || {};
  const sections = Array.isArray(data.sectionList) ? data.sectionList : [];
  const filtered = sections.filter((s) => s.id !== sectionId);
  // reassign order
  const reindexed = filtered.map((s, idx) => ({ ...s, order: idx }));
  await ref.set({ sectionList: reindexed, updatedAt: new Date() }, { merge: true });
};

export const setSectionThreads = async (issueId, sectionId, threadIds) => {
  const ref = getIssueRef(issueId);
  const snap = await ref.get();
  if (!snap.exists) return;
  const data = snap.data() || {};
  const sections = Array.isArray(data.sectionList) ? data.sectionList : [];
  const unique = Array.from(new Set((Array.isArray(threadIds) ? threadIds : []).map((v) => String(v))));
  const next = sections.map((s) => (s.id === sectionId ? { ...s, threadIdList: unique, updatedAt: new Date() } : s));
  await ref.set({ sectionList: next, updatedAt: new Date() }, { merge: true });
};

export const reorderSections = async (issueId, orderedSectionIds) => {
  const ref = getIssueRef(issueId);
  const snap = await ref.get();
  if (!snap.exists) return;
  const data = snap.data() || {};
  const sections = Array.isArray(data.sectionList) ? data.sectionList : [];
  const idToSection = new Map(sections.map((s) => [s.id, s]));
  const ordered = (Array.isArray(orderedSectionIds) ? orderedSectionIds : [])
    .map((id) => idToSection.get(id))
    .filter(Boolean)
    .map((s, idx) => ({ ...s, order: idx }));
  // include any missing sections at the end (safety)
  const missing = sections.filter((s) => !ordered.some((o) => o.id === s.id));
  const finalList = [...ordered, ...missing.map((s, idx) => ({ ...s, order: ordered.length + idx }))];
  await ref.set({ sectionList: finalList, updatedAt: new Date() }, { merge: true });
};

// Optional helper for UI-side validation (not enforced by write APIs)
export const getIssueItemCollectionIds = async (issueId) => {
  const issueRef = getIssueRef(issueId);
  const itemsSnap = await issueRef.collection('items').get();
  return Array.from(new Set(itemsSnap.docs.map((d) => String(d.data().collectionId)).filter(Boolean)));
};


